/**
 * Symbol catalogue for point symbology, line/fill style definitions, and the
 * default style a vector layer is drawn in before anyone picks one.
 *
 * Symbols are plain SVG path data on a 24×24 viewBox so the SAME definition
 * renders both the gallery swatch (React) and the map marker (an OL Icon
 * style, as a data-URI) — no duplicated artwork and no react-dom/server
 * dependency.
 */

import type { LayerRenderer, RendererCategory } from '../types';

export type SymbolCategory =
  | 'Basic'
  | 'Administration'
  | 'Utility'
  | 'Infrastructure'
  | 'Land use'
  | 'Alerts';

export interface PointSymbolDef {
  id: string;
  name: string;
  category: SymbolCategory;
  /** SVG path data on a 0 0 24 24 viewBox. */
  path: string;
  /**
   * Equivalent SLD `WellKnownName`. Only symbols with one can be reproduced by
   * GeoServer, so the gallery filters to these for WMS layers.
   */
  sldMark?: string;
}

export const POINT_SYMBOLS: PointSymbolDef[] = [
  /* ── Basic ───────────────────────────────────────────────────────────── */
  {
    id: 'circle',
    name: 'Circle',
    category: 'Basic',
    path: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16z',
    sldMark: 'circle',
  },
  { id: 'square', name: 'Square', category: 'Basic', path: 'M5 5h14v14H5z', sldMark: 'square' },
  {
    id: 'triangle',
    name: 'Triangle',
    category: 'Basic',
    path: 'M12 4 21 20H3z',
    sldMark: 'triangle',
  },
  { id: 'diamond', name: 'Diamond', category: 'Basic', path: 'M12 3 21 12 12 21 3 12z' },
  {
    id: 'star',
    name: 'Star',
    category: 'Basic',
    path: 'M12 3l2.6 6.1 6.6.6-5 4.3 1.5 6.4L12 17l-5.7 3.4 1.5-6.4-5-4.3 6.6-.6z',
    sldMark: 'star',
  },
  {
    id: 'cross',
    name: 'Cross',
    category: 'Basic',
    path: 'M10 3h4v7h7v4h-7v7h-4v-7H3v-4h7z',
    sldMark: 'cross',
  },
  {
    id: 'x',
    name: 'X',
    category: 'Basic',
    path: 'M5.6 3 12 9.4 18.4 3 21 5.6 14.6 12 21 18.4 18.4 21 12 14.6 5.6 21 3 18.4 9.4 12 3 5.6z',
    sldMark: 'x',
  },
  {
    id: 'pin',
    name: 'Map pin',
    category: 'Basic',
    path: 'M12 2c-3.9 0-7 3.1-7 7 0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z',
  },

  /* ── Administration ──────────────────────────────────────────────────── */
  {
    id: 'administrator',
    name: 'Administrator',
    category: 'Administration',
    path: 'M12 3a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7zm0 9c4 0 7 2 7 4.5V21H5v-4.5C5 14 8 12 12 12z',
  },
  {
    id: 'government',
    name: 'Government office',
    category: 'Administration',
    path: 'M12 3 22 8v2H2V8l10-5zM4 11h3v7H4v-7zm6.5 0h3v7h-3v-7zM17 11h3v7h-3v-7zM2 19h20v2H2v-2z',
  },
  {
    id: 'municipal',
    name: 'Municipal office',
    category: 'Administration',
    path: 'M6 9h12v12H6V9zm5-7h1l5 2-5 2v2h-1V2zM9 12h2v3H9v-3zm4 0h2v3h-2v-3z',
  },
  {
    id: 'public-building',
    name: 'Public building',
    category: 'Administration',
    path: 'M3 21V9l9-6 9 6v12h-6v-7H9v7H3z',
  },
  {
    id: 'police',
    name: 'Police',
    category: 'Administration',
    path: 'M12 2l8 3v6c0 5-3.4 9.2-8 11-4.6-1.8-8-6-8-11V5l8-3zm0 5-1.3 3.1-3.3.3 2.5 2.2-.7 3.3L12 14.2l2.8 1.7-.7-3.3 2.5-2.2-3.3-.3L12 7z',
  },
  {
    id: 'fire-station',
    name: 'Fire station',
    category: 'Administration',
    path: 'M12 2c1 3.5-1.5 5-1.5 7.5A3 3 0 0 0 13 12c1.5 0 2.5-1 2.5-2.5C18 11 19 13.5 19 15a7 7 0 1 1-14 0c0-4 3.5-6 4.5-9C10 4 11 3 12 2z',
  },

  /* ── Utility ─────────────────────────────────────────────────────────── */
  {
    id: 'gas',
    name: 'Gas',
    category: 'Utility',
    path: 'M12 2c.5 3-2 4.5-2 7a2.5 2.5 0 0 0 5 0c2 1.5 3 3.5 3 5.5a6 6 0 1 1-12 0c0-3.5 3-5.5 4-8 .5-1.3 1.5-2.5 2-4.5z',
  },
  {
    id: 'water',
    name: 'Water',
    category: 'Utility',
    path: 'M12 2.5S5 10 5 14.5a7 7 0 0 0 14 0C19 10 12 2.5 12 2.5z',
  },
  {
    id: 'electricity',
    name: 'Electricity',
    category: 'Utility',
    path: 'M13 2 4 14h6l-1 8 9-12h-6l1-8z',
  },
  {
    id: 'telecom',
    name: 'Telecom',
    category: 'Utility',
    path: 'M12 8a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm-1 5.9h2L16 22h-2.4l-1.6-5-1.6 5H8l3-8.1zM6.5 3.5 8 5a6 6 0 0 0 0 10l-1.5 1.5a8 8 0 0 1 0-13zm11 0a8 8 0 0 1 0 13L16 15a6 6 0 0 0 0-10l1.5-1.5z',
  },
  {
    id: 'sewer',
    name: 'Sewer',
    category: 'Utility',
    path: 'M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM9 8h6v2H9V8zm-2 3h10v2H7v-2zm2 3h6v2H9v-2z',
  },
  {
    id: 'utility',
    name: 'Utility facility',
    category: 'Utility',
    path: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm8.9 3 1.6 1.2-1.6 2.8-1.9-.6c-.5.4-1.1.7-1.7 1l-.4 2h-3.8l-.4-2c-.6-.3-1.2-.6-1.7-1l-1.9.6-1.6-2.8L7.1 12 5.5 10.8l1.6-2.8 1.9.6c.5-.4 1.1-.7 1.7-1l.4-2h3.8l.4 2c.6.3 1.2.6 1.7 1l1.9-.6 1.6 2.8L20.9 12z',
  },

  /* ── Infrastructure ──────────────────────────────────────────────────── */
  {
    id: 'school',
    name: 'School',
    category: 'Infrastructure',
    path: 'M12 3 1 9l11 6 9-4.9V17h2V9L12 3zM5 13.2V17c0 1.7 3.1 3 7 3s7-1.3 7-3v-3.8l-7 3.8-7-3.8z',
  },
  {
    id: 'hospital',
    name: 'Hospital',
    category: 'Infrastructure',
    path: 'M9 3h6v6h6v6h-6v6H9v-6H3V9h6V3z',
  },
  {
    id: 'bus-stop',
    name: 'Bus stop',
    category: 'Infrastructure',
    path: 'M6 3h12a3 3 0 0 1 3 3v9a2 2 0 0 1-2 2v2h-3v-2H8v2H5v-2a2 2 0 0 1-2-2V6a3 3 0 0 1 3-3zm0 4v4h12V7H6zm1.5 6a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm9 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z',
  },
  {
    id: 'railway',
    name: 'Railway',
    category: 'Infrastructure',
    path: 'M7 2h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3l2 3h-3l-1.5-3h-5L8 21H5l2-3a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3zM6 6v5h12V6H6zm2.5 7a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm7 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z',
  },
  {
    id: 'airport',
    name: 'Airport',
    category: 'Infrastructure',
    path: 'M21 15v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V8l-8 5v2l8-2.5V17l-2 1.5V20l3.5-1 3.5 1v-1.5L13 17v-4.5L21 15z',
  },
  {
    id: 'parking',
    name: 'Parking',
    category: 'Infrastructure',
    path: 'M5 3h9a6 6 0 0 1 0 12h-4v6H5V3zm5 4v4h3a2 2 0 0 0 0-4h-3z',
  },
  {
    id: 'shelter',
    name: 'Shelter',
    category: 'Infrastructure',
    path: 'M12 3 2 11h3v10h14V11h3L12 3zm0 6a3 3 0 1 1 0 6 3 3 0 0 1 0-6z',
  },
  {
    id: 'public-facility',
    name: 'Public facility',
    category: 'Infrastructure',
    path: 'M4 21V10l8-6 8 6v11h-5v-6h-6v6H4zm8-14a2 2 0 1 1 0 4 2 2 0 0 1 0-4z',
  },

  /* ── Land use ────────────────────────────────────────────────────────── */
  {
    id: 'residential',
    name: 'Residential',
    category: 'Land use',
    path: 'M12 3 2 11h3v10h6v-6h2v6h6V11h3L12 3z',
  },
  {
    id: 'commercial',
    name: 'Commercial',
    category: 'Land use',
    path: 'M4 4h16l1.5 5A3 3 0 0 1 19 12v9H5v-9a3 3 0 0 1-2.5-3L4 4zm4 10h4v5H8v-5zm7 0h3v3h-3v-3z',
  },
  {
    id: 'industrial',
    name: 'Industrial',
    category: 'Land use',
    path: 'M2 21V9l6 4V9l6 4V3h8v18H2zm5-4v2h2v-2H7zm5 0v2h2v-2h-2zm5 0v2h2v-2h-2z',
  },
  {
    id: 'landmark',
    name: 'Landmark',
    category: 'Land use',
    path: 'M12 2l3 5h-2v9h4v3H7v-3h4V7H9l3-5z',
  },

  /* ── Alerts ──────────────────────────────────────────────────────────── */
  {
    id: 'warning',
    name: 'Warning',
    category: 'Alerts',
    path: 'M12 2 23 21H1L12 2zm-1 6v7h2V8h-2zm0 9v2h2v-2h-2z',
  },
  {
    id: 'information',
    name: 'Information',
    category: 'Alerts',
    path: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1 5h2v2h-2V7zm0 4h2v7h-2v-7z',
  },

  /* ── Utility: Oil & Gas ──────────────────────────────────────────────
   * Names follow the Esri "Oil & Gas" symbol sheet so field data keyed to
   * that vocabulary maps straight across. The artwork is drawn here in the
   * gallery's own 24x24 single-colour style — it is not Esri artwork, and
   * these are silhouettes rather than the two-tone badges on the sheet,
   * because a symbol renders with one fill colour.
   *
   * No `sldMark`: none of these has an SLD WellKnownName equivalent, so the
   * gallery correctly hides them for GeoServer WMS layers (server-rendered)
   * and offers them for client-drawn vector layers.
   */
  {
    id: 'og-compressor',
    name: 'Compressor',
    category: 'Utility',
    path: 'M1 10h5v4H1zM18 10h5v4h-5zM12 6.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11z',
  },
  {
    id: 'og-compressor-station',
    name: 'Compressor Station',
    category: 'Utility',
    path: 'M1 10.5h4v3H1zM19 10.5h4v3h-4zM8 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zM16 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9z',
  },
  {
    id: 'og-excess-flow-valve',
    name: 'Excess Flow Valve',
    category: 'Utility',
    path: 'M3 5l8 7-8 7zM21 5l-8 7 8 7z',
  },
  {
    id: 'og-gas-lamp',
    name: 'Gas Lamp',
    category: 'Utility',
    path: 'M10 2h4v3h-4zM8.4 5h7.2l-1.7 7h-3.8zM11 12h2v7h-2zM7.5 19h9v3h-9z',
  },
  {
    id: 'og-meter-setting',
    name: 'Meter Setting',
    category: 'Utility',
    path: 'M11 2h2v4h-2zM5.5 6h13v10h-13zM11 16h2v5h-2z',
  },
  {
    id: 'og-pressure-monitoring-device',
    name: 'Pressure Monitoring Device',
    category: 'Utility',
    path: 'M12 2.5a8 8 0 1 0 0 16 8 8 0 0 0 0-16zM10.8 19.5h2.4V22h-2.4z',
  },
  {
    id: 'og-regulator',
    name: 'Regulator',
    category: 'Utility',
    path: 'M1 13h22v3.5H1zM12 4a7 7 0 0 0-7 7h14a7 7 0 0 0-7-7z',
  },
  {
    id: 'og-regulator-station',
    name: 'Regulator Station',
    category: 'Utility',
    path: 'M12 2.5A6.5 6.5 0 0 0 5.5 9h13A6.5 6.5 0 0 0 12 2.5zM3 9.5h18V13H3zM6 13h12v8H6z',
  },
  {
    id: 'og-relief-valve',
    name: 'Relief Valve',
    category: 'Utility',
    path: 'M12 1.5l4.2 6.2H7.8zM3 10l7.2 5L3 20zM21 10l-7.2 5L21 20z',
  },
  {
    id: 'og-rural-tap',
    name: 'Rural Tap',
    category: 'Utility',
    path: 'M3 7h10v4.5H3zM13 4.5h3.5v9.5H13zM7.5 11.5h3.5v6.5H7.5zM5.5 18h7.5v3H5.5z',
  },
  {
    id: 'og-scrubber',
    name: 'Scrubber',
    category: 'Utility',
    path: 'M8.5 2h7A1.5 1.5 0 0 1 17 3.5v17a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 7 20.5v-17A1.5 1.5 0 0 1 8.5 2z',
  },
  {
    id: 'og-strainer',
    name: 'Strainer',
    category: 'Utility',
    path: 'M2.5 4h19l-7.5 8.5V21l-4-2.2v-6.3z',
  },
  {
    id: 'og-town-border-station',
    name: 'Town Border Station',
    category: 'Utility',
    path: 'M12 2.5 22.5 10v2.5h-21V10zM5 12.5h14V21H5z',
  },
  {
    id: 'og-valve-closed',
    name: 'Valve Closed',
    category: 'Utility',
    path: 'M3 6l8 6-8 6zM21 6l-8 6 8 6zM10.9 1.5h2.2v21h-2.2z',
  },
  {
    id: 'og-valve-open',
    name: 'Valve Open',
    category: 'Utility',
    path: 'M3 8.5l8 5-8 5zM21 8.5l-8 5 8 5zM10.9 2h2.2v6.5h-2.2zM7.5 1h9v2.4h-9z',
  },
  {
    id: 'og-wellhead',
    name: 'Wellhead',
    category: 'Utility',
    path: 'M10.9 2h2.2v18h-2.2zM6 5.5h12v2.4H6zM8 11h8v2.4H8zM4 20h16v2.5H4z',
  },
  {
    id: 'og-anode',
    name: 'Anode',
    category: 'Utility',
    path: 'M4 9.5h16v6.5H4zM9 4h6v5.5H9z',
  },
  {
    id: 'og-bond-junction',
    name: 'Bond Junction',
    category: 'Utility',
    path: 'M6 10.5h12v3H6zM4 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7zM20 8.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z',
  },
  {
    id: 'og-rectifier',
    name: 'Rectifier',
    category: 'Utility',
    path: 'M13.5 1.5 5 13h5l-1.5 9.5L18 10.5h-5z',
  },
  {
    id: 'og-test-point',
    name: 'Test Point',
    category: 'Utility',
    path: 'M10.9 6h2.2v16h-2.2zM6.5 1.5h11V6h-11z',
  },
  {
    id: 'og-3-way-tee',
    name: '3-Way Tee',
    category: 'Utility',
    path: 'M1 10h22v4H1zM10 14h4v8h-4z',
  },
  {
    id: 'og-coupling',
    name: 'Coupling',
    category: 'Utility',
    path: 'M1 10h22v4H1zM6.8 6.5h2.4v11H6.8zM14.8 6.5h2.4v11h-2.4z',
  },
  {
    id: 'og-dehydration-equipment',
    name: 'Dehydration Equipment',
    category: 'Utility',
    path: 'M12 1.5c3.4 4.8 5 6.8 5 9.3a5 5 0 1 1-10 0c0-2.5 1.6-4.5 5-9.3zM4 19.5h16V22H4z',
  },
  {
    id: 'og-drip',
    name: 'Drip',
    category: 'Utility',
    path: 'M12 2c3.8 5.4 6 7.8 6 10.3a6 6 0 1 1-12 0C6 9.8 8.2 7.4 12 2z',
  },
  {
    id: 'og-elbow',
    name: 'Elbow',
    category: 'Utility',
    path: 'M1.5 10h12.5v4H1.5zM10 10h4v12h-4z',
  },
  {
    id: 'og-electro-stop',
    name: 'Electro Stop',
    category: 'Utility',
    path: 'M1 10.5h6.5v3H1zM16.5 10.5H23v3h-6.5zM14 2 8.5 13H12l-1.2 9L16 11h-3.4z',
  },
  {
    id: 'og-end-cap',
    name: 'End Cap',
    category: 'Utility',
    path: 'M1 10h14v4H1zM15 8a4 4 0 0 1 0 8z',
  },
  {
    id: 'og-expansion-joint',
    name: 'Expansion Joint',
    category: 'Utility',
    path: 'M1 10.5h3.5v3H1zM19.5 10.5H23v3h-3.5zM5.5 6h1.8v12H5.5zM8.4 6h1.8v12H8.4zM11.3 6h1.8v12h-1.8zM14.2 6H16v12h-1.8zM17.1 6h1.4v12h-1.4z',
  },
  {
    id: 'og-flange',
    name: 'Flange',
    category: 'Utility',
    path: 'M1 10h22v4H1zM8.8 4h2.4v16H8.8zM12.8 4h2.4v16h-2.4z',
  },
  {
    id: 'og-line-heater',
    name: 'Line Heater',
    category: 'Utility',
    path: 'M3 16.5h18V21H3zM12 1.5c2.6 3.2 3.7 4.6 3.7 6.6a3.7 3.7 0 1 1-7.4 0c0-2 1.1-3.4 3.7-6.6z',
  },
  {
    id: 'og-odorizer',
    name: 'Odorizer',
    category: 'Utility',
    path: 'M10 1.5h4V5h-4zM7.5 5h9v13a3.5 3.5 0 0 1-3.5 3.5h-2A3.5 3.5 0 0 1 7.5 18z',
  },
  {
    id: 'og-plastic-fusion',
    name: 'Plastic Fusion',
    category: 'Utility',
    path: 'M1 10h9.5v4H1zM13.5 10H23v4h-9.5zM10.4 6.5h3.2v11h-3.2z',
  },
  {
    id: 'og-reducer',
    name: 'Reducer',
    category: 'Utility',
    path: 'M1 7.5h8v9H1zM9 7.5l6 2.6v3.8L9 16.5zM15 10h8v4h-8z',
  },
  {
    id: 'og-screw',
    name: 'Screw',
    category: 'Utility',
    path: 'M3.5 3.5h6v17h-6zM9.5 6.5h11v3.5h-11zM9.5 13h11v3.5h-11z',
  },
  {
    id: 'og-short-stop',
    name: 'Short Stop',
    category: 'Utility',
    path: 'M8 2h8l6 6v8l-6 6H8l-6-6V8z',
  },
  {
    id: 'og-tank',
    name: 'Tank',
    category: 'Utility',
    path: 'M5 6a7 3.2 0 0 1 14 0v12a7 3.2 0 0 1-14 0z',
  },
  {
    id: 'og-tee',
    name: 'Tee',
    category: 'Utility',
    path: 'M1 8h22v5H1zM9.5 13h5v9h-5z',
  },
  {
    id: 'og-transition',
    name: 'Transition',
    category: 'Utility',
    path: 'M1 7.5h10.5v9H1zM11.5 10H23v4H11.5z',
  },
  {
    id: 'og-weld',
    name: 'Weld',
    category: 'Utility',
    path: 'M1 10h9v4H1zM14 10h9v4h-9zM12 7.4a4.6 4.6 0 1 0 0 9.2 4.6 4.6 0 0 0 0-9.2z',
  },
  {
    id: 'og-coating',
    name: 'Coating',
    category: 'Utility',
    path: 'M3.5 3h15v6.5h-15zM10.8 9.5h2.4V14h-2.4zM7.5 14h9v8h-9z',
  },
  {
    id: 'og-corrosion',
    name: 'Corrosion',
    category: 'Utility',
    path: 'M12 1l2.2 5.3 5.3-2.2-2.2 5.3 5.3 2.2-5.3 2.2 2.2 5.3-5.3-2.2L12 22.4l-2.2-5.3-5.3 2.2 2.2-5.3L1.4 11.8l5.3-2.2-2.2-5.3 5.3 2.2z',
  },
  {
    id: 'og-damaged-meter',
    name: 'Damaged Meter',
    category: 'Utility',
    path: 'M4.5 3h6.2l-2.2 5.4h3.8L9 21H4.5zM13 3h6.5v18H13z',
  },
  {
    id: 'og-dead-vegetation',
    name: 'Dead Vegetation',
    category: 'Utility',
    path: 'M10.9 11h2.2v11h-2.2zM12 1.5 5.5 11H10v2.4h4V11h4.5z',
  },
  {
    id: 'og-gas-leak-1',
    name: 'Gas Leak 1',
    category: 'Utility',
    path: 'M12 8.5c1.9 2.6 2.7 3.6 2.7 5a2.7 2.7 0 1 1-5.4 0c0-1.4.8-2.4 2.7-5z',
  },
  {
    id: 'og-gas-leak-2',
    name: 'Gas Leak 2',
    category: 'Utility',
    path: 'M12 5c2.7 3.8 3.9 5.3 3.9 7.3a3.9 3.9 0 1 1-7.8 0c0-2 1.2-3.5 3.9-7.3z',
  },
  {
    id: 'og-gas-leak-3',
    name: 'Gas Leak 3',
    category: 'Utility',
    path: 'M12 1.5c3.5 5 5.2 7 5.2 9.7a5.2 5.2 0 1 1-10.4 0c0-2.7 1.7-4.7 5.2-9.7z',
  },
  {
    id: 'og-gas-leak-inspection',
    name: 'Gas Leak Inspection',
    category: 'Utility',
    path: 'M9 1.5c2.8 3.9 4.1 5.5 4.1 7.6a4.1 4.1 0 1 1-8.2 0c0-2.1 1.3-3.7 4.1-7.6zM16.5 12a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zM19.6 19.2 22.5 22l-1.7 1.7-2.9-2.8z',
  },
  {
    id: 'og-inspection-note',
    name: 'Inspection Note',
    category: 'Utility',
    path: 'M9 1h6v3H9zM5 3h3v3h8V3h3v19H5z',
  },
  {
    id: 'og-no-access',
    name: 'No Access',
    category: 'Utility',
    path: 'M6 10h12v11.5H6zM12 1.5a5 5 0 0 0-5 5V10h2.6V6.5a2.4 2.4 0 0 1 4.8 0V10H17V6.5a5 5 0 0 0-5-5z',
  },
  {
    id: 'og-odor',
    name: 'Odor',
    category: 'Utility',
    path: 'M3.5 5.5c3-2.2 5.2 2 8.5 0s5.5-2.2 8.5 0v3.2c-3-2.2-5.2 2-8.5 0s-5.5-2.2-8.5 0zM3.5 13c3-2.2 5.2 2 8.5 0s5.5-2.2 8.5 0v3.2c-3-2.2-5.2 2-8.5 0s-5.5-2.2-8.5 0z',
  },
  {
    id: 'og-one-call-ticket',
    name: 'One Call Ticket',
    category: 'Utility',
    path: 'M1.5 6h21v4.2a2 2 0 0 0 0 3.6V18h-21v-4.2a2 2 0 0 0 0-3.6z',
  },
  {
    id: 'og-pipe-job-separator',
    name: 'Pipe Job Separator',
    category: 'Utility',
    path: 'M1.5 12 7 6.5v3.3h10V6.5L22.5 12 17 17.5v-3.3H7v3.3z',
  },
  {
    id: 'og-pipeline-marker',
    name: 'Pipeline Marker',
    category: 'Utility',
    path: 'M5.5 1.5h2.6V22H5.5zM8.1 2.6h11l-3.2 4.4 3.2 4.4h-11z',
  },
  {
    id: 'og-squeeze-off',
    name: 'Squeeze Off',
    category: 'Utility',
    path: 'M2.5 2.5h4.2v8.2L11 12l-4.3 1.3v8.2H2.5zM21.5 2.5h-4.2v8.2L13 12l4.3 1.3v8.2h4.2z',
  },
  {
    id: 'og-valve-inspection',
    name: 'Valve Inspection',
    category: 'Utility',
    path: 'M1.5 5.5 8.5 10l-7 4.5zM15.5 5.5 8.5 10l7 4.5zM17 12.5a4.2 4.2 0 1 0 0 8.4 4.2 4.2 0 0 0 0-8.4zM20 19.4l2.5 2.5-1.6 1.6-2.5-2.5z',
  },
  {
    id: 'og-work-order',
    name: 'Work Order',
    category: 'Utility',
    path: 'M4 1.5h9.5L19 7v7.5H4zM13.5 1.5V7H19zM14.5 18.5l4.6-4.6 2.4 2.4-4.6 4.6h-2.4z',
  },
];

export const SYMBOL_CATEGORIES: SymbolCategory[] = [
  'Basic',
  'Administration',
  'Utility',
  'Infrastructure',
  'Land use',
  'Alerts',
];

export function getPointSymbol(id: string | undefined): PointSymbolDef {
  return POINT_SYMBOLS.find((symbol) => symbol.id === id) ?? POINT_SYMBOLS[0];
}

/**
 * The style a unique-value category actually draws with: its own colour
 * (always), plus any symbol fields it overrides, with every field it does
 * NOT override falling back to the renderer's global value. This is the one
 * place that merge happens, so the map, the legend, and GeoServer's SLD all
 * agree on what a given category looks like.
 */
export function mergeCategoryStyle(renderer: LayerRenderer, category: RendererCategory): LayerRenderer {
  return {
    ...renderer,
    color: category.color,
    symbolId: category.symbolId ?? renderer.symbolId,
    sldMark: category.sldMark ?? renderer.sldMark,
    pointSize: category.pointSize ?? renderer.pointSize,
    rotation: category.rotation ?? renderer.rotation,
    outlineColor: category.outlineColor ?? renderer.outlineColor,
    outlineWidth: category.outlineWidth ?? renderer.outlineWidth,
    fillOpacity: category.fillOpacity ?? renderer.fillOpacity,
    lineStyle: category.lineStyle ?? renderer.lineStyle,
    fillStyle: category.fillStyle ?? renderer.fillStyle,
    outlineStyle: category.outlineStyle ?? renderer.outlineStyle,
  };
}

/* ── Default style ─────────────────────────────────────────────────────── */

/**
 * How a vector layer is drawn before anyone chooses symbology.
 *
 * SHARED ON PURPOSE. The map applies this to every client-drawn layer with no
 * renderer of its own — an imported file, an ArcGIS FeatureServer — and the
 * legend has to show the same thing. Kept as one exported object rather than
 * two matching literals, because the copy that drifts is the one nobody
 * notices until the key disagrees with the map.
 */
export const DEFAULT_RENDERER: LayerRenderer = {
  type: 'single',
  color: '#3B82F6',
  fillOpacity: 0.25,
  outlineColor: '#1D4ED8',
  outlineWidth: 2,
  pointSize: 5,
  defaultColor: '#94A3B8',
};

/* ── Line styles ───────────────────────────────────────────────────────── */

export type LineStyleId = 'solid' | 'dashed' | 'dotted' | 'dash-dot' | 'long-dash' | 'short-dash';

export interface LineStyleDef {
  id: LineStyleId;
  name: string;
  /** SVG/Leaflet dash pattern; undefined means a continuous line. */
  dashArray?: string;
}

export const LINE_STYLES: LineStyleDef[] = [
  { id: 'solid', name: 'Solid' },
  { id: 'dashed', name: 'Dashed', dashArray: '8,6' },
  { id: 'dotted', name: 'Dotted', dashArray: '1,5' },
  { id: 'dash-dot', name: 'Dash-dot', dashArray: '12,5,2,5' },
  { id: 'long-dash', name: 'Long dash', dashArray: '18,8' },
  { id: 'short-dash', name: 'Short dash', dashArray: '4,4' },
];

export function getLineDashArray(style: LineStyleId | undefined): string | undefined {
  return LINE_STYLES.find((entry) => entry.id === style)?.dashArray;
}

/* ── Fill styles ───────────────────────────────────────────────────────── */

export type FillStyleId = 'solid' | 'transparent';

export const FILL_STYLES: { id: FillStyleId; name: string }[] = [
  { id: 'solid', name: 'Solid' },
  { id: 'transparent', name: 'Transparent' },
];

/**
 * Builds the marker SVG for a point symbol. Shared by the gallery preview and
 * the map's own Icon style so both always look identical.
 */
export function buildSymbolSvg(options: {
  path: string;
  size: number;
  color: string;
  outlineColor: string;
  outlineWidth: number;
  opacity: number;
  rotation: number;
}): string {
  const { path, size, color, outlineColor, outlineWidth, opacity, rotation } = options;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24"
    style="display:block;opacity:${opacity};transform:rotate(${rotation}deg);overflow:visible">
    <path d="${path}" fill="${color}" stroke="${outlineColor}" stroke-width="${outlineWidth}"
      stroke-linejoin="round" vector-effect="non-scaling-stroke" />
  </svg>`;
}
